import React, { useState } from "react";
import { ChevronLeft } from "lucide-react";
import BrainBankListView from "./BrainBankListView";
import CreateBrainBank from "./CreateBrainBank";
import AssignUsers from "./AssignUsers";
import ViewBrainBankFiles from "./ViewBrainBankFiles";
import UploadFiles from "./UploadFiles";
import EditBrainBank from "./EditBrainBank";

// Define BrainBank type
interface BrainBank {
  id: string;
  knowledgebase_name: string;
  index_name: string;
  description: string;
  file_ids: string[];
  last_sync: string;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
  summary: string | null;
  files?: Array<{
    id: string;
    file_name: string;
    status: string;
    created_by: string;
    created_at: string;
  }>;
}

const BrainBankController: React.FC = () => {
  const [selectedBrainBank, setSelectedBrainBank] = useState<BrainBank | null>(null);
  const [view, setView] = useState<"list" | "create" | "edit" | "files" | "upload">("list");
  const [refreshKey, setRefreshKey] = useState(0);
  
  const handleCreateClick = () => {
    setSelectedBrainBank(null);
    setView("create");
  };
  
 const handleEditClick = (brainBank: BrainBank) => {
  setSelectedBrainBank(brainBank);
  setView("edit");
};
  
  const handleViewFilesClick = (brainBank: BrainBank) => {
    setSelectedBrainBank(brainBank);
    setView("files");
  };
  
  const handleUploadClick = (brainBank: BrainBank) => {
    setSelectedBrainBank(brainBank);
    setView("upload");
  };
  
  const handleBackToList = () => {
    setView("list");
    setSelectedBrainBank(null);
    setRefreshKey(prev => prev + 1); 
  };
  
  const handleSaveComplete = () => {
    setView("list");
    setSelectedBrainBank(null);
    setRefreshKey(prev => prev + 1); 
  };
  
  const handleKnowledgeBaseRefresh = () => {
    // Only refresh the data, don't change the view
    setRefreshKey(prev => prev + 1);
  };
  
  if (view === "create") {
  return (
    <div className="mx-auto w-full max-w-screen-xl">
      {/* Sticky Back Button */}
      <div className="sticky top-0 z-20 bg-white shadow-sm px-4 py-3">
        <button 
          onClick={handleBackToList}
          className="flex items-center text-violet-600 hover:text-violet-800 transition-colors font-medium"
        >
          <ChevronLeft className="h-5 w-5 mr-1" />
          Back to BrainBanks
        </button>
      </div>
      
      {/* Form Content */}
      <div className="p-4">
        <CreateBrainBank onKnowledgeBaseChange={handleKnowledgeBaseRefresh} />
      </div>
    </div>
  );
}
  
  if (view === "edit") {
  return (
    <div className="mx-auto w-full max-w-screen-xl">
      {/* Sticky Back Button */}
      <div className="sticky top-0 z-20 bg-white shadow-sm px-4 py-3">
        <button 
          onClick={handleBackToList}
          className="flex items-center text-violet-600 hover:text-violet-800 transition-colors font-medium"
        >
          <ChevronLeft className="h-5 w-5 mr-1" />
          Back to BrainBanks
        </button>
      </div>
      
      {/* Form Content */}
      <div className="p-4">
        <EditBrainBank brainBank={selectedBrainBank!} onRefresh={handleSaveComplete} />
      </div>
    </div>
  );
}
  
  if (view === "files") {
  return (
    <div className="mx-auto w-full max-w-screen-xl">
      {/* Sticky Back Button */}
      <div className="sticky top-0 z-50 bg-white shadow-sm px-4 py-3">
        <button 
          onClick={handleBackToList}
          className="flex items-center text-violet-600 hover:text-violet-800 transition-colors font-medium"
        >
          <ChevronLeft className="h-5 w-5 mr-1" />
          Back to BrainBanks
        </button>
      </div>
      
      {/* Content */}
      <div className="p-4">
        <ViewBrainBankFiles brainBank={selectedBrainBank} onRefresh={handleSaveComplete} />
      </div>
    </div>
  );
}
  
  if (view === "upload") {
  return (
    <div className="mx-auto w-full max-w-screen-xl">
      {/* Sticky Back Button */}
      <div className="sticky top-0 z-20 bg-white shadow-sm px-4 py-3">
        <button 
          onClick={handleBackToList}
          className="flex items-center text-violet-600 hover:text-violet-800 transition-colors font-medium"
        >
          <ChevronLeft className="h-5 w-5 mr-1" />
          Back to BrainBanks
        </button>
      </div>
      
      {/* Content */}
      <div className="p-4">
        <UploadFiles refresh={refreshKey} />
      </div>
    </div>
  );
}
  
  return (
    <BrainBankListView 
      onCreateClick={handleCreateClick}
      onEditClick={handleEditClick}
      onViewFilesClick={handleViewFilesClick}
      onUploadClick={handleUploadClick}
      refreshKey={refreshKey}
    />
  );
};

export default BrainBankController;